import { useRef, useState, useEffect, useMemo } from 'react'

interface EmailContentFrameProps {
  html: string
  className?: string
  darkMode?: boolean
}

/**
 * Renders HTML email content in an isolated iframe
 * - Preserves original email styling (designed emails show their colors)
 * - Plain/simple emails adapt to dark theme
 * - Auto-resizes to fit content height
 */
export function EmailContentFrame({ html, className, darkMode = true }: EmailContentFrameProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const [height, setHeight] = useState(400)

  // Detect if email has designed/styled content with explicit colors/backgrounds
  // We look for actual color/background styling, not just any inline style
  const hasDesignedContent = useMemo(() => {
    const lowerHtml = html.toLowerCase()
    return (
      // Background colors (common in designed emails)
      lowerHtml.includes('background-color') ||
      lowerHtml.includes('background:') ||
      lowerHtml.includes('bgcolor') ||
      // Explicit color styling
      /color\s*[:=]\s*[#"']/.test(lowerHtml) ||
      // Table-based email templates (usually have styling)
      (lowerHtml.includes('<table') && (lowerHtml.includes('width') || lowerHtml.includes('cellpadding'))) ||
      // Centered container layouts (common in marketing emails)
      lowerHtml.includes('<center')
    )
  }, [html])

  // Build isolated HTML document
  // Always set light text as default in dark mode - inline styles will override for designed parts
  const srcdoc = useMemo(() => {
    const baseStyles = darkMode ? `
      * { box-sizing: border-box; }
      body {
        margin: 0;
        padding: 24px;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
        font-size: 14px;
        line-height: 1.7;
        background: transparent;
        color: #d4d4d8;
      }
      a { color: #60a5fa; }
      img { max-width: 100%; height: auto; }
      table { border-collapse: collapse; }
      blockquote {
        margin: 8px 0;
        padding-left: 12px;
        border-left: 3px solid #52525b;
        color: #a1a1aa;
      }
      /* Gmail quote styling */
      .gmail_quote {
        color: #a1a1aa;
      }
      pre, code {
        background: #27272a;
        border-radius: 4px;
        font-family: 'SF Mono', Monaco, 'Courier New', monospace;
        font-size: 13px;
        color: #e4e4e7;
      }
      pre { padding: 12px; overflow-x: auto; }
      code { padding: 2px 4px; }
      hr { border: none; border-top: 1px solid #3f3f46; margin: 16px 0; }
    ` : `
      * { box-sizing: border-box; }
      body {
        margin: 0;
        padding: 24px;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
        font-size: 14px;
        line-height: 1.7;
        background: #ffffff;
        color: #1f2937;
      }
      a { color: #2563eb; }
      img { max-width: 100%; height: auto; }
      table { border-collapse: collapse; }
    `

    return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <style>${baseStyles}</style>
</head>
<body>${html}</body>
</html>
    `.trim()
  }, [html, darkMode])

  // Auto-resize iframe to match content height
  useEffect(() => {
    const iframe = iframeRef.current
    if (!iframe) return

    const updateHeight = () => {
      try {
        const doc = iframe.contentDocument
        if (doc?.body) {
          // Get the actual content height
          const contentHeight = Math.max(
            doc.body.scrollHeight,
            doc.body.offsetHeight,
            doc.documentElement?.scrollHeight || 0,
            doc.documentElement?.offsetHeight || 0
          )
          setHeight(contentHeight + 32) // Add padding
        }
      } catch (e) {
        // Cross-origin error - keep default height
        console.warn('Could not access iframe content:', e)
      }
    }

    // Update height on load
    const handleLoad = () => {
      updateHeight()
      // Also check after images load
      const doc = iframe.contentDocument
      if (doc) {
        const images = doc.getElementsByTagName('img')
        Array.from(images).forEach(img => {
          if (!img.complete) {
            img.addEventListener('load', updateHeight)
          }
        })
      }
    }

    iframe.addEventListener('load', handleLoad)
    return () => iframe.removeEventListener('load', handleLoad)
  }, [html])

  return (
    <iframe
      ref={iframeRef}
      srcDoc={srcdoc}
      sandbox="allow-same-origin"
      title="Email content"
      style={{
        height: `${height}px`,
        width: '100%',
        border: 'none',
        display: 'block',
      }}
      className={className}
    />
  )
}
